import { useCreateCheckoutSession, ApiFetchError } from "@workspace/api-client-react";

// Set right before sending a signed-out visitor to sign-up so we can resume
// checkout automatically once they're authenticated.
export const SUBSCRIBE_INTENT_KEY = "palnote_subscribe_intent";

// Origin + Vite base path (no trailing slash). The server validates this against
// its allowed hosts before using it to build Stripe return URLs.
export function getReturnBaseUrl(): string {
  return window.location.origin + import.meta.env.BASE_URL.replace(/\/$/, "");
}

// Translate a failed checkout start into user-facing toast copy. A 409 means
// the server intentionally refused (already subscribed, or a checkout is
// already open) — surface its explanation instead of a generic retry message.
export function checkoutErrorToast(err: unknown): {
  title: string;
  description: string;
} {
  if (err instanceof ApiFetchError && err.status === 409) {
    const serverMessage =
      err.data && typeof err.data === "object"
        ? (err.data as { error?: string }).error
        : undefined;
    return {
      title: "Checkout not needed",
      description:
        serverMessage ??
        "You already have an active subscription or open checkout.",
    };
  }
  return {
    title: "Could not start checkout",
    description: "Please try again in a moment.",
  };
}

// Starts a Stripe Checkout Session and redirects the browser to the hosted page.
export function useStartCheckout() {
  const mutation = useCreateCheckoutSession();
  const start = async (): Promise<void> => {
    const res = await mutation.mutateAsync({
      data: { returnBaseUrl: getReturnBaseUrl() },
    });
    if (res.url) {
      window.location.href = res.url;
    }
  };
  return { start, isPending: mutation.isPending };
}
